import { safelyRender, sanitizeHeaderText } from '@liveauctioneers/caterwaul-components/lib/utils/safelyRender';
import { CaretRight, PencilSimple } from '@liveauctioneers/hammer-ui-core/icons';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { TextInput } from '@liveauctioneers/hammer-ui-core/textInput';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { memo, useState, ChangeEvent, FocusEvent, KeyboardEvent } from 'react';
import isEqual from 'react-fast-compare';

type FolderProps = {
    readonly activeFolder: number | null;
    readonly archive?: boolean;
    readonly count: number;
    readonly edit?: boolean;
    readonly id: number | string;
    readonly loading: boolean;
    readonly name: string;
    readonly onArchiveFolder?: Function;
    readonly onRenameFolder?: Function;
    readonly onSetActiveInbox: Function;
    readonly renaming?: boolean;
    readonly setArchive?: Function;
    readonly setRenaming?: Function;
    readonly testid?: string;
};

const FolderComponent = ({
    activeFolder,
    archive,
    count,
    edit,
    id,
    loading,
    name: existingName,
    onArchiveFolder = () => {},
    onRenameFolder = () => {},
    onSetActiveInbox,
    renaming,
    setArchive = () => {},
    setRenaming = () => {},
    testid = 'messengerFolder',
}: FolderProps) => {
    const active = activeFolder === id;
    const [name, setName] = useState(existingName);

    let inner = (
        <div className="flex items-center">
            <InlineButton
                className={cn(
                    'justify-between py-[6px] hover:no-underline focus-visible:no-underline',
                    edit ? 'w-[calc(100%-82px)]' : 'w-full'
                )}
                disabled={loading}
                onClick={() => !loading && onSetActiveInbox(id)}
                style={active ? 'primary' : 'neutral'}
            >
                <BodySecondary
                    className={cn(
                        'block w-[170px] truncate text-left',
                        active ? 'text-link-primary' : 'text-text-secondary'
                    )}
                    title={sanitizeHeaderText(name)}
                >
                    {safelyRender({ html: name, wrapper: 'span' })}
                </BodySecondary>
                {!edit &&
                    (loading && active ? (
                        <Loading size={24} />
                    ) : (
                        <BodySecondary className="text-text-secondary">{count ? count : ''}</BodySecondary>
                    ))}
            </InlineButton>
            {edit && (
                <div className="flex items-center gap-xs">
                    <InlineButton
                        className="hover:bg-background-hover hover:no-underline"
                        onClick={() => setArchive(true)}
                        size="sm"
                    >
                        Archive
                    </InlineButton>
                    {' | '}
                    <PencilSimple
                        className="h-[14px] w-[14px] cursor-pointer text-primary-500 hover:text-primary-400"
                        onClick={() => setRenaming(true)}
                        size="xs"
                    />
                </div>
            )}
        </div>
    );

    // @ts-ignore < should work not work figuring out exact type for now
    const handleFocus = (event: FocusEvent<HTMLElement>) => event.target.select();

    const handleKeyPress = (event: KeyboardEvent<HTMLElement>) => {
        if (event.key === 'Enter') {
            onRenameFolder(id, name.trim());
            setRenaming(false);
        }
        event.stopPropagation();
    };

    const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
        const target = event.target;
        setName(target.value);
        event.stopPropagation();
    };

    const handleButtonClick = () => {
        if (name.trim()) {
            onRenameFolder(id, name.trim());
            setRenaming(false);
        }
    };

    if (archive) {
        inner = (
            <div className="-mx-xs flex w-[204px] flex-row items-center justify-between bg-surface-100 px-xs">
                <BodySecondary>Archive?</BodySecondary>
                <div className="inline-flex max-h-[34px] shrink items-center justify-between">
                    <InlineButton
                        onClick={() => {
                            onArchiveFolder(id);
                            setArchive(false);
                            if (active) {
                                onSetActiveInbox(0);
                            }
                        }}
                        size="sm"
                    >
                        Yes
                    </InlineButton>
                    <BodySecondary>&nbsp;|&nbsp;</BodySecondary>
                    <InlineButton
                        onClick={() => {
                            setArchive(false);
                            setRenaming(false);
                        }}
                        size="sm"
                    >
                        Cancel
                    </InlineButton>
                </div>
            </div>
        );
    } else if (renaming) {
        inner = (
            <div className="-mx-xs flex w-[224px] flex-row items-center justify-between px-xs">
                <TextInput
                    aria-label="New folder name"
                    id="newFolder"
                    onChange={handleOnChange}
                    onFocus={handleFocus}
                    onKeyUp={handleKeyPress}
                    placeholder="New folder name"
                    value={name}
                />
                <div
                    className="[&_svg]:text-white flex h-[34px] min-w-[34px] cursor-pointer items-center justify-center bg-primary-500 hover:bg-primary-400"
                    onClick={handleButtonClick}
                >
                    <CaretRight size="xs" />
                </div>
            </div>
        );
    }

    return (
        <li
            className={cn('list-none px-xs', active ? 'bg-surface-100' : 'bg-white', 'hover:bg-surface-100')}
            data-testid={testid}
        >
            {inner}
        </li>
    );
};

export const Folder = memo<FolderProps>(FolderComponent, isEqual);
